import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { message, Popconfirm, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { EditOutlined } from "@ant-design/icons";
import { DeleteIcon } from "assets/icons";
import {
  useUserDeleteMutation,
  useUserListQuery,
} from "@ticketingplatform/api/dist/users/hooks";
import { pageToSkip } from "utils/tableUtils";
import { User } from "@ticketingplatform/api/dist/users/__generated__/fragments";
import { generatePath, Link } from "react-router-dom";
import { GET_USERS } from "@ticketingplatform/api/dist/users";
import { ROUTE_PATHS } from "const/paths";
import useHasPermissions from "hooks/useHasPermissions";
import {
  Permission,
  UserRole,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import { GetUsersVariables } from "@ticketingplatform/api/dist/users/__generated__/queries";

import { UseUserDeleteReturnType, UseUserListReturnType } from "./types";

export const useUserDelete = (): UseUserDeleteReturnType => {
  const intl = useIntl();
  const [deleteUser, { loading }] = useUserDeleteMutation({
    evictQueries: [GET_USERS],
    onCompleted: () =>
      message.success(
        intl.formatMessage({
          defaultMessage: "User has been removed!",
        })
      ),
    onError: () =>
      message.error(
        intl.formatMessage({
          defaultMessage: "Cannot remove this User",
        })
      ),
  });

  return useMemo(
    () => ({
      onConfirm(id) {
        deleteUser({ variables: { id } });
      },
      loading,
    }),
    [deleteUser, loading]
  );
};

export const useUserActionColumns = (): ColumnsType<User> => {
  const intl = useIntl();
  const canEdit = useHasPermissions([Permission.USER_UPDATE]);
  const canDelete = useHasPermissions([Permission.USER_DELETE]);
  const { onConfirm, loading } = useUserDelete();

  return useMemo(
    () =>
      canEdit || canDelete
        ? [
            {
              title: intl.formatMessage({
                defaultMessage: "Actions",
              }),
              key: "action",
              render: (_, { id }) => (
                <Space size="middle">
                  {canEdit && (
                    <Link
                      to={generatePath(ROUTE_PATHS.SettingsUserEdit, {
                        id: id.toString(),
                      })}
                    >
                      <EditOutlined />
                    </Link>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title={intl.formatMessage({
                        defaultMessage:
                          "Are you sure you want to delete this User?",
                      })}
                      onConfirm={() => onConfirm(id)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]
        : [],
    [canDelete, canEdit, intl, loading, onConfirm]
  );
};

export const useUserList = (): UseUserListReturnType => {
  const [{ page, pageSize, orderBy }, setTableQueryParams] =
    useTableQueryParams<User>();

  const variables = useMemo<GetUsersVariables>(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      orderBy,
      filters: { role: { equals: UserRole.ADMIN } },
    }),
    [orderBy, page, pageSize]
  );

  const { data, loading } = useUserListQuery({
    variables,
  });
  const onChange = useCallback<UseUserListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.users || {};

  return useMemo(
    () => ({
      loading,
      pagination: {
        pageSize,
        current: page,
        total,
      },
      dataSource,
      onChange,
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
